'use client';
import { cva, cx } from 'cva';
import React from 'react';
import Typography from '@mui/material/Typography';
import { useSpring, animated } from '@react-spring/web';
import Loader from '@/components/Styled/Loader';

const button = cva('button', {
  variants: {
    variant: {
      filled: ['px-4 py-2 lg:px-7 rounded-[24px]'],
      outlined: ['px-4 py-2 lg:px-7 rounded-[24px]'],
      plain: ['px-4 py-2 lg:px-7 bg-transparent hover:bg-body-background'],
      icon: [
        'p-2 bg-transparent hover:bg-body-background disabled:bg-transparent rounded-full',
      ],
      text: ['px-4 py-2 lg:px-7 bg-transparent'],
      chip: [
        'px-3 py-2 rounded-2xl text-text-primary text-14 font-dmsans font-medium',
      ],
    },
    color: {
      primary: [],
      secondary: [],
      transparent: [],
    },
    selected: {
      true: [],
      false: [],
    },
    disabled: {
      true: ['cursor-default'],
      false: ['cursor-pointer'],
    },
  },
  compoundVariants: [
    {
      variant: 'filled',
      disabled: true,
      className: 'bg-gray-300 text-text-disabled',
    },
    {
      variant: 'filled',
      color: 'primary',
      className: 'bg-primary text-white text-button',
    },
    {
      variant: 'filled',
      color: 'secondary',
      className: 'bg-secondary text-white text-button',
    },
    {
      variant: 'outlined',
      disabled: true,
      className:
        'border border-body-background text-text-disabled bg-white text-button',
    },
    {
      variant: 'outlined',
      color: 'primary',
      disabled: false,
      className:
        'border border-primary text-primary bg-white text-button hover:bg-primary-light-1',
    },
    {
      variant: 'outlined',
      color: 'secondary',
      disabled: false,
      className: 'border border-secondary text-secondary bg-white text-button',
    },
    {
      variant: 'text',
      selected: true,
      className: 'text-text-primary',
    },
    {
      variant: 'text',
      selected: false,
      className: 'text-text-disabled hover:text-text-primary',
    },
    {
      variant: 'chip',
      color: 'secondary',
      selected: true,
      className: 'border border-transparent bg-chips-secondary',
    },
    {
      variant: 'chip',
      color: 'secondary',
      selected: false,
      className: 'border border-chips-secondary bg-transparent',
    },
    {
      variant: 'chip',
      color: 'primary',
      selected: true,
      className: 'border border-transparent bg-chips-primary',
    },
    {
      variant: 'chip',
      color: 'primary',
      selected: false,
      className: 'border border-chips-primary bg-transparent',
    },
  ],
});
function StyledButtonNew(props) {
  const {
    variant = 'filled',
    color = 'primary',
    loading = false,
    className,
    children,
    selected = false,
    disabled = false,
    onClick,
    ...rest
  } = props;

  const [showLoader, setShowLoader] = React.useState(false);

  const buttonRef = React.useRef(null);
  const handleClick = (event) => {
    const btn = buttonRef.current;
    const circle = document.createElement('span');
    let bgClass = 'bg-ripple';
    if (variant !== 'filled') {
      if (color === 'primary') {
        bgClass = 'bg-primary-light-1';
      } else if (color === 'secondary') {
        bgClass = 'bg-secondary-light-1';
      }
    }
    if (btn && circle) {
      const diameter = Math.max(btn.clientWidth, btn.clientHeight);
      const radius = diameter / 2;
      const { left, top } = btn.getBoundingClientRect();
      circle.style.width = circle.style.height = `${diameter}px`;
      circle.style.left = `${event.clientX - (left + radius)}px`;
      circle.style.top = `${event.clientY - (top + radius)}px`;
      circle.style.transform = 'scale(0)';
      circle.classList.add(
        'ripple',
        'absolute',
        'rounded-full',
        'animation-ripple',
        'animate-ripple',
        bgClass,
      );
      const ripple = btn.getElementsByClassName('ripple')[0];
      if (ripple) {
        ripple.remove();
      }
      btn.appendChild(circle);
    }

    if (loading) {
      return;
    }

    if (onClick) {
      onClick(event);
    }
  };

  React.useEffect(() => {
    if (loading) {
      setShowLoader(true);
    } else {
      setShowLoader(false);
    }
  }, [loading]);

  const fadeOutProps = useSpring({ opacity: showLoader ? 1 : 0 });

  return (
    <button
      ref={buttonRef}
      className={button({
        variant: variant,
        color: color,
        selected: selected,
        className: cx(
          className,
          `relative overflow-hidden ${
            loading ? 'cursor-not-allowed' : 'cursor-pointer'
          }`,
        ),
        disabled: disabled,
      })}
      disabled={disabled}
      onClick={handleClick}
      {...rest}>
      <Typography
        variant={variant}
        className="styled-btn-text "
        sx={{ display: showLoader ? 'none' : 'block' }}>
        {children}
      </Typography>
      {showLoader && (
        <animated.div className="loader-container" style={fadeOutProps}>
          <Loader />
        </animated.div>
      )}
    </button>
  );
}
export default StyledButtonNew;
